import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import TertiaryButton from "../../Buttons/TertiaryButton/TertiaryButton";
import InlineIconButton from "../../Buttons/InlineIconButton/InlineIconButton";
import useBuildLedgerRows from "../../../hooks/useBuildLedgerRows/useBuildLedgerRows";
import useCarrierLedgerCharges from "../../../hooks/useCarrierLedgerCharges/useCarrierLedgerCharges";

export default function LoadProfileLedger({
  activeLoad,
  activeUser,
  carrierExtraPay,
  carrierExtraPayLoading,
  handleTableAction
}) {
  const { ledgerDetailRows } = useBuildLedgerRows(activeLoad, carrierExtraPay);
  const { carrierAccessorials } = useCarrierLedgerCharges(carrierExtraPay, activeLoad);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [tableHeight, setTableHeight] = useState(0);
  const [tableWidth, setTableWidth] = useState(window.innerWidth - 45);

  const columns = useMemo(
    () => [
      {
        Header: "Description",
        accessor: "description",
        filter: "fuzzyText",
        width: 400,
        Cell: DefaultCell
      },
      {
        Header: "Customer Charge",
        accessor: "customerCharge",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 200,
        Cell: CurrencyCell
      },
      {
        Header: "Carrier Cost",
        accessor: "carrierCost",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 200,
        Cell: CurrencyCell
      },
      {
        Header: "Other Cost",
        accessor: "otherCost",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 200,
        Cell: CurrencyCell
      },
      {
        Header: "Profit",
        accessor: "profit",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 200,
        Cell: CurrencyCell
      },
    ]
  );

  const handleQuickActions = (editAccessorials = false) => {
    if (editAccessorials) localStorage.setItem("accessorials", JSON.stringify(carrierAccessorials));

    handleTableAction({ action: "quick-actions", tab: "1", loadId: activeLoad.id });
  };

  useEffect(() => {
    setIsLoading(carrierExtraPayLoading);
  }, [carrierExtraPayLoading]);

  useEffect(() => {
    setLedger(ledgerDetailRows.filter(({ isVisible }) => isVisible));
  }, [ledgerDetailRows]);

  useEffect(() => {
    if (isExpanded) {
      setLedger(ledgerDetailRows.filter(({ description }) => description !== "Other"));
    } else {
      setLedger(ledgerDetailRows.filter(({ isVisible }) => isVisible));
    }
  }, [isExpanded]);

  useEffect(() => {
    setTableHeight(ledger.length * 32);
  }, [ledger]);

  useEffect(() => {
    const handleWindowResize = () => {
      setTableWidth(window.innerWidth - 45);
    };

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return (
    <Box className="Load-profile-component">
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: 1, marginX: "20px" }} >
            <Box sx={{ display: "flex", paddingBottom: "10px" }}>
              <Typography variant="h3" marginRight="10px">
                Ledger
              </Typography>
              <Box sx={{ marginRight: "10px" }}>
                <InlineIconButton
                  onClick={() => handleQuickActions(true)}
                  icon={EditOutlinedIcon}
                  buttonColor="blue"
                  disabled={activeLoad.settlementDate !== null && (activeUser.roleId !== 1 && activeUser.roleId !== 2 && activeUser.roleId !== 8)}
                />
              </Box>
              <PrimaryButton
                label="Add"
                onClick={() => handleQuickActions()}
                loading={isLoading}
                size="small"
                disabled={activeLoad.settlementDate !== null && (activeUser.roleId !== 1 && activeUser.roleId !== 2 && activeUser.roleId !== 8)}
              />
            </Box>
            <CustomizedTable
              id="load-profile-ledger"
              columns={columns}
              data={ledger}
              defaultHeight={tableHeight}
              defaultWidth={tableWidth}
            />
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <TertiaryButton
                label={isExpanded ? "Collapse Details" : "View Details"}
                onClick={() => setIsExpanded(!isExpanded)}
                startIcon={isExpanded ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                color="primary.60"
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

LoadProfileLedger.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  activeUser: PropTypes.object.isRequired,
  carrierExtraPay: PropTypes.array.isRequired,
  carrierExtraPayLoading: PropTypes.bool.isRequired,
  handleTableAction: PropTypes.func
};